import { createContext, useContext } from "react";
import en from "@/locales/en/messages.json";
import es from "@/locales/es/messages.json";
import fr from "@/locales/fr/messages.json";

export type Locale = "en" | "es" | "fr";
type Messages = Record<string, string>;

const locales: Record<Locale, Messages> = { en, es, fr };

export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  es: "Espa\u00f1ol",
  fr: "Fran\u00e7ais",
};

const STORAGE_KEY = "scroll-locale";

export function getSavedLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && saved in locales) return saved as Locale;
  } catch {}
  const browserLang = navigator.language.split("-")[0];
  if (browserLang in locales) return browserLang as Locale;
  return "en";
}

export function saveLocale(locale: Locale) {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {}
}

export function t(key: string, locale: Locale = "en", params?: Record<string, string | number>): string {
  let message = locales[locale]?.[key] || locales.en[key] || key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      message = message.replace(`{${k}}`, String(v));
    }
  }
  return message;
}

export interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

export const I18nContext = createContext<I18nContextType>({
  locale: "en",
  setLocale: () => {},
  t: (key) => t(key, "en"),
});

export const useI18n = () => useContext(I18nContext);
